import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Avatar, Box, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useGetMainCardActivity } from "@/cards/common/api/cardactivity.api";

type CardEngagementAccordionProps = {
  cardId?: number;
  cardVisits: number;
  contentViews: number;
};

type ActivityUser = {
  id: number;
  username: string;
  first_name?: string;
  last_name?: string;
  profile_image?: string;
};

type ActivityEvent = {
  user: ActivityUser;
  event_date: string;
};

type UserSummary = {
  user: ActivityUser;
  count: number;
};

type StatCardProps = {
  icon: React.ReactNode;
  value: number;
  label: string;
  isOpen: boolean;
  position: "left" | "right";
  onClick: () => void;
};

const StatCard = ({ icon, value, label, isOpen, position, onClick }: StatCardProps) => {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        minWidth: 0,
        minHeight: 102,
        p: "var(--spacing-3x)",
        textAlign: "left",
        cursor: "pointer",
        border: "var(--stroke-xs) solid",
        borderColor: isOpen ? "var(--card-dark-border-active)" : "transparent",
        borderRadius:
          position === "left"
            ? "var(--spacing-4x) 0 var(--spacing-4x) 0"
            : "0 var(--spacing-4x) 0 var(--spacing-4x)",
        backgroundColor: "#241D13",
        transition: "border-color 160ms ease, background-color 160ms ease",
        "&:hover": {
          backgroundColor: "var(--card-dark-surface)",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: "var(--spacing-2x)",
        }}
      >
        <Box
          sx={{
            width: 30,
            height: 30,
            display: "grid",
            placeItems: "center",
            border: "var(--stroke-xs) solid var(--card-dark-border)",
            borderRadius: "50%",
            color: isOpen ? "var(--card-dark-accent)" : "var(--card-dark-text-muted)",
          }}
        >
          {icon}
        </Box>

        <ArrowForwardRoundedIcon
          sx={{
            fontSize: 16,
            color: isOpen ? "var(--card-dark-accent)" : "var(--card-dark-text-muted)",
            transform: isOpen ? "rotate(90deg)" : "none",
            transition: "transform 160ms ease",
          }}
        />
      </Box>

      <Typography
        sx={{
          font: "var(--semibold-md)",
          color: "var(--card-dark-text)",
        }}
      >
        {value.toLocaleString()}
      </Typography>

      <Typography
        sx={{
          mt: "var(--spacing-2x)",
          font: "var(--body-xs)",
          color: "var(--neutral-400)",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

const UserRow = ({ summary, countLabel }: { summary: UserSummary; countLabel: string }) => {
  const { user } = summary;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "var(--spacing-2x)",
        py: "var(--spacing-2x)",
        borderBottom: "var(--stroke-xs) solid var(--neutral-700)",
      }}
    >
      <Avatar
        src={user.profile_image || undefined}
        alt={user.username}
        sx={{
          width: 30,
          height: 30,
          font: "var(--body-sm)",
        }}
      >
        {user.first_name?.charAt(0)}
      </Avatar>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          gap: "var(--spacing-1x)",
        }}
      >
        <Typography
          sx={{
            font: "var(--semibold-sm)",
            color: "var(--card-dark-text)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {user.first_name} {user.last_name}
        </Typography>

        <Typography
          sx={{
            font: "var(--body-xs)",
            color: "var(--neutral-400)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          @{user.username}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          textAlign: "right",
          gap: "var(--spacing-1x)",
        }}
      >
        <Typography
          sx={{
            font: "var(--semibold-sm)",
            color: "var(--card-dark-text)",
          }}
        >
          {summary.count}
        </Typography>

        <Typography
          sx={{
            font: "var(--body-xs)",
            color: "var(--neutral-200)",
          }}
        >
          {countLabel}
        </Typography>
      </Box>
    </Box>
  );
};

const groupEventsByUser = (events: ActivityEvent[]): UserSummary[] => {
  const userMap = new Map<number, UserSummary>();

  events.forEach((event) => {
    const userId = event.user.id;
    const existing = userMap.get(userId);

    if (existing) {
      existing.count += 1;
    } else {
      userMap.set(userId, {
        user: event.user,
        count: 1,
      });
    }
  });

  return Array.from(userMap.values());
};

const CardEngagementAccordion = ({
  cardId,
  cardVisits,
  contentViews,
}: CardEngagementAccordionProps) => {
  const { t } = useTranslation();

  const resolvedCardId = Number(cardId);
  const hasValidCardId = Number.isFinite(resolvedCardId) && resolvedCardId > 0;

  const [openCard, setOpenCard] = useState<"visits" | "views" | null>(null);

  const visitsIsOpen = openCard === "visits";
  const viewsIsOpen = openCard === "views";

  // Fetch only when the matching accordion panel is open
  const { data: visitsData } = useGetMainCardActivity({
    key: ["main_card_viewed", resolvedCardId],
    params: {
      id: resolvedCardId,
      type: "main_card_viewed",
    },
    options: {
      enabled: hasValidCardId && visitsIsOpen,
    },
  });

  const { data: viewsData } = useGetMainCardActivity({
    key: ["main_card_opened", resolvedCardId],
    params: {
      id: resolvedCardId,
      type: "main_card_opened",
    },
    options: {
      enabled: hasValidCardId && viewsIsOpen,
    },
  });

  const visitEvents: ActivityEvent[] = visitsData?.data ?? [];
  const viewEvents: ActivityEvent[] = viewsData?.data ?? [];

  const visitorList = useMemo(() => groupEventsByUser(visitEvents), [visitEvents]);
  const viewerList = useMemo(() => groupEventsByUser(viewEvents), [viewEvents]);

  const activeList = visitsIsOpen ? visitorList : viewsIsOpen ? viewerList : [];

  const handleClick = (card: "visits" | "views") => {
    setOpenCard(openCard === card ? null : card);
  };

  const listTitle = visitsIsOpen
    ? t("deck_summary_visitors", { defaultValue: "Visitors" })
    : t("deck_summary_viewers", { defaultValue: "Viewers" });

  const listSubtitle = visitsIsOpen
    ? t("deck_summary_viewers_subtitle", {
        defaultValue: "People who have viewed content on this card",
      })
    : t("deck_summary_visitors_subtitle", {
        defaultValue: "People who have opened this card",
      });

  const countLabel = visitsIsOpen
    ? t("deck_summary_visit_count_label", { defaultValue: "Visits" })
    : t("deck_summary_view_count_label", { defaultValue: "Views" });

  return (
    <Box
      sx={{
        width: "100%",
        px: 0,
        py: "var(--spacing-3x)",
      }}
    >
      <Box
        sx={{
          mb: "var(--spacing-2x)",
        }}
      >
        <Typography
          sx={{
            font: "var(--semibold-xxs)",
            color: "#807356",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
          }}
        >
          {t("deck_summary_card_activity", {
            defaultValue: "Card activity",
          })}
        </Typography>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, 1fr)",
          gap: "var(--spacing-5x)",
        }}
      >
        <StatCard
          icon={<VisibilityOutlinedIcon sx={{ fontSize: 17 }} />}
          value={cardVisits}
          label={t("deck_summary_card_visits", {
            defaultValue: "Card Visits",
          })}
          isOpen={visitsIsOpen}
          position="left"
          onClick={() => handleClick("visits")}
        />

        <StatCard
          icon={<PlayCircleOutlineRoundedIcon sx={{ fontSize: 17 }} />}
          value={contentViews}
          label={t("deck_summary_content_views", {
            defaultValue: "Content Views",
          })}
          isOpen={viewsIsOpen}
          position="right"
          onClick={() => handleClick("views")}
        />
      </Box>

      {(visitsIsOpen || viewsIsOpen) && (
        <Box
          sx={{
            mt: "var(--spacing-5x)",
          }}
        >
          <Box
            sx={{
              pb: "var(--spacing-3x)",
              gap: "var(--spacing-2x)",
              borderBottom: "var(--stroke-xs) solid var(--neutral-700)",
            }}
          >
            <Typography
              sx={{
                font: "var(--semibold-md)",
                color: "var(--neutralWhite)",
              }}
            >
              {listTitle}
            </Typography>

            <Typography
              sx={{
                mt: "2px",
                font: "var(--body-xs)",
                color: "var(--neutral-400)",
              }}
            >
              {listSubtitle}
            </Typography>
          </Box>

          <Box
            sx={{
              maxHeight: 500,
              overflowY: "auto",
            }}
          >
            {activeList.map((summary) => (
              <UserRow key={summary.user.id} summary={summary} countLabel={countLabel} />
            ))}

            {activeList.length === 0 && (
              <Typography
                sx={{
                  p: "var(--spacing-3x)",
                  textAlign: "center",
                  font: "var(--body-xxs)",
                  color: "var(--card-dark-text-secondary)",
                }}
              >
                {t("deck_summary_no_activity_users", {
                  defaultValue: "No users yet",
                })}
              </Typography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CardEngagementAccordion;
